import { useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@ts-core/lib/apollo';

import { useMutationCompletionHandler, getErrorMessage } from '@ts-core/lib/apollo';

import { getMediaItemByIdQuery, getMediaItemsBySubjectQuery } from '../graphql/queries';
import { updateMediaItemMutation, createExternalMediaItemMutation } from '../graphql/mutations';

export default function useMediaItem({ courseId, subjectId, mediaItemId, onMediaItemUpdated, onExternalMediaItemCreated }) {
  const { onError, addItemToCache } = useMutationCompletionHandler();

  /**
   * Get Media Item by Id
   */
  const { data, loading, error } = useQuery(getMediaItemByIdQuery, {
    variables: { courseId, subjectId, mediaItemId },
    skip: !courseId || !subjectId || !mediaItemId,
  });
  const mediaItem = useMemo(() => data?.mediaItem || {}, [data]);

  /**
   * Update media item
   */
  const [updateMediaItem, { loading: updatingMediaItem, error: updateMediaItemError }] = useMutation(updateMediaItemMutation, {
    onCompleted: onMediaItemUpdated,
  });
  const onUpdateMediaItem = useCallback(
    (mediaItemId, mediaItemInput) => {
      const { name, availableFrom, availableUpTo, withExpiration } = mediaItemInput;

      const input = {
        name,
        availableFrom: withExpiration ? availableFrom : undefined,
        availableUpTo: withExpiration ? availableUpTo : undefined,
        withExpiration,
      };

      updateMediaItem({
        variables: {
          courseId,
          subjectId,
          mediaItemId,
          input,
        },
      });
    },
    [updateMediaItem, courseId, subjectId]
  );

  /**
   * Create external media item
   */
  const [createExternalMediaItem, { loading: creatingExternalMediaItem, error: createExternalMediaItemError }] = useMutation(
    createExternalMediaItemMutation,
    {
      onError,
      onCompleted: onExternalMediaItemCreated,
      update: addItemToCache({
        query: getMediaItemsBySubjectQuery,
        queryField: 'mediaItems',
        variables: { courseId, subjectId },
        mutationField: 'createExternalMediaItem',
      }),
    }
  );
  const onCreateExternalMediaItem = useCallback(
    (input) => {
      createExternalMediaItem({
        variables: {
          courseId,
          subjectId,
          input,
        },
      });
    },
    [createExternalMediaItem, courseId, subjectId]
  );

  return {
    onCreateExternalMediaItem,
    creatingExternalMediaItem,
    createExternalMediaItemError: getErrorMessage({
      error: createExternalMediaItemError,
    }),

    onUpdateMediaItem,
    updatingMediaItem,
    updateMediaItemError: getErrorMessage({ error: updateMediaItemError }),

    mediaItem,
    loading,
    error: getErrorMessage({ error }),
  };
}
